/* eslint-disable jsx-a11y/control-has-associated-label */
import { useCallback, useContext, useEffect, useMemo } from "react";
import { Row } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useFieldArray, useFormContext } from "react-hook-form";

import { useGetQuotationFormConfig } from "api/queries/quotation-form/useGetQuotationFormConfig";
import { useGetCurrencies } from "api/queries/common/useGetCurrencies";
import { prefillChargeCodes } from "helpers/quotationFormHelper";
import Button from "components/atoms/Button/Button";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { getObjectByKeys, roundUp } from "helpers/commonHelpers";
import CostFormChargeItem from "./CostFormChargeItem";
import CostFormCalculationContext from "./CostCalculationContext";

const currencyDataSelector = (data: any) =>
  data.map((currency: any) => ({
    key: currency.id,
    label: currency.code,
    name: currency.name,
    value: currency.code,
    code: currency.code,
  }));

type CostFormChargesProps = {
  chargeName: string;
};

const quotationConfigSelector = (data: any, selectedFreightMode: string) => {
  const clonedData = { ...data };
  if (data?.measurement_units) {
    const selectedFreightModeData = data.freight_modes.find((item: any) => item.value === selectedFreightMode);
    const allMeasurementUnits: any = [];
    const perContainerUnits: any = [];
    data.measurement_units.forEach((unitData: any) => {
      const updatedData = {
        ...unitData,
        value: unitData.key,
      };
      if (/\b\d+ft\b/i.test(unitData.label) || ["Custom Container Size", "Less than container"].includes(unitData.label)) {
        perContainerUnits.push(updatedData);
      } else {
        allMeasurementUnits.push(updatedData);
      }
    });
    if (
      ["ocean_freight", "road_freight"].includes(selectedFreightMode) ||
      ["ocean", "road"].includes(selectedFreightModeData?.type)
    ) {
      allMeasurementUnits.push({ label: "Per Container", value: "", children: perContainerUnits });
    }
    clonedData.measurement_units = allMeasurementUnits;
  }
  return clonedData;
};

function CostFormCharges(props: CostFormChargesProps) {
  const intl = useIntl();
  const { chargeName } = props;
  const id = useParams().id?.toUpperCase();
  const { control, watch, setValue } = useFormContext();
  const selectedFreightMode = watch("service.freight_mode");
  const { data: quotationConfigData } = useGetQuotationFormConfig(id!, {
    enabled: !!id,
    refetchOnMount: false,
    select: useCallback((data) => quotationConfigSelector(data, selectedFreightMode), [selectedFreightMode]),
  });

  const selectedCustomFreightMode = quotationConfigData?.freight_modes.some(
    (item: any) => item?.value === selectedFreightMode && item?.is_custom_freight && ["air", "road"].includes(item?.type),
  );

  const IsChargeableWeightRequired =
    selectedCustomFreightMode || ["nfo", "road_freight", "regular_air"].includes(selectedFreightMode);

  const { data: quoteRequestDetails } = useGetQuoteRequestDetails({
    shipmentId: id!,
    options: { enabled: !!id, refetchOnMount: false },
  });
  const { data: currencies = [] } = useGetCurrencies({ select: currencyDataSelector, refetchOnMount: false });
  const { fields, append, remove } = useFieldArray({ name: `costs.${chargeName}`, control });
  const watchFieldArray = watch(`costs.${chargeName}`);
  const controlledFields = fields?.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));
  const {
    calculateTotalCost,
    totalFreightCharges,
    calculatingFreightCharges,
    totalOtherCharges,
    calculatingOtherCharges,
    shipperPreferredCurrency,
  } = useContext<any>(CostFormCalculationContext);

  const currentCustomChargeCodeConfig = useMemo(() => {
    if (!selectedFreightMode || !quotationConfigData?.custom_charge_codes) {
      return {};
    }
    if (chargeName === "other_charges") {
      const otherCharges = [
        ...(quotationConfigData.custom_charge_codes[selectedFreightMode]?.origin_charges || []),
        ...(quotationConfigData.custom_charge_codes[selectedFreightMode]?.destination_charges || []),
      ];
      return getObjectByKeys(otherCharges);
    }
    return getObjectByKeys([...(quotationConfigData.custom_charge_codes[selectedFreightMode]?.main_freight_charges || [])]);
  }, [selectedFreightMode, quotationConfigData, chargeName]);

  const currentChargeCosts: any = {
    main_freight_charges: {
      subTotal: totalFreightCharges,
      isCostLoading: calculatingFreightCharges,
    },
    other_charges: {
      subTotal: totalOtherCharges,
      isCostLoading: calculatingOtherCharges,
    },
  };

  useEffect(() => {
    if (!selectedFreightMode || !quotationConfigData) {
      return;
    }
    let customChargeCodes;
    if (chargeName === "other_charges") {
      customChargeCodes = [
        ...(quotationConfigData?.custom_charge_codes?.[selectedFreightMode]?.origin_charges || []).map((item: any) => ({
          ...item,
          applies_to: "origin",
        })),
        ...(quotationConfigData?.custom_charge_codes?.[selectedFreightMode]?.destination_charges || []).map((item: any) => ({
          ...item,
          applies_to: "destination",
        })),
      ];
    } else {
      customChargeCodes = [...(quotationConfigData?.custom_charge_codes?.[selectedFreightMode]?.main_freight_charges || [])];
    }
    let preFillData = prefillChargeCodes(
      controlledFields,
      customChargeCodes,
      quotationConfigData?.assigned_team?.currency || "",
    );
    if (
      selectedFreightMode === "obc" &&
      chargeName === "main_freight_charges" &&
      !preFillData.find((item) => item.description === "Total excess luggage cost")
    ) {
      preFillData.unshift({
        description: "Total excess luggage cost",
        measurement_unit: "",
        amount: "",
        quantity: "",
        currency_code: quotationConfigData?.assigned_team?.currency || "",
      });
    } else if (
      selectedFreightMode !== "obc" &&
      chargeName === "main_freight_charges" &&
      preFillData.find((item) => item.description === "Total excess luggage cost")
    ) {
      preFillData = preFillData.filter((item) => item.description !== "Total excess luggage cost");
    }
    setValue(`costs.${chargeName}`, preFillData);
    calculateTotalCost(chargeName);
  }, [selectedFreightMode, quotationConfigData, chargeName]);

  const handleAddChargeClick = () => {
    append({
      description: "",
      measurement_unit: "",
      amount: "",
      quantity: "",
      currency_code: quotationConfigData?.assigned_team?.currency || "",
      ...(chargeName === "other_charges" && { applies_to: "" }),
    });
  };

  return (
    <div>
      <table className="w-100 quotation-cost-table">
        <thead className="text-secondary">
          <tr>
            <th>
              <FormattedMessage id="quotation-form.chargeName" defaultMessage="Charge name" />
            </th>
            {chargeName === "other_charges" ? (
              <th>
                <FormattedMessage id="quotation-form.appliesTo" defaultMessage="Applies to" />
              </th>
            ) : (
              <th style={{ minWidth: 140 }} />
            )}
            <th>
              <FormattedMessage id="quotation-form.measurementUnit" defaultMessage="Measurement unit" />
            </th>
            <th>
              <FormattedMessage id="quotation-form.unitCost" defaultMessage="Unit cost" />
            </th>
            <th>
              <FormattedMessage id="quotation-form.quantity" defaultMessage="Quantity" />
            </th>
            <th>
              <FormattedMessage id="quotation-form.currency" defaultMessage="Currency" />
            </th>
            <th>
              <FormattedMessage id="quotation-form.totalCost" defaultMessage="Total cost" />
            </th>
          </tr>
        </thead>
        <tbody>
          {controlledFields &&
            controlledFields.map((field, index) => (
              <CostFormChargeItem
                key={field.id}
                config={quotationConfigData}
                currentCustomChargeCode={currentCustomChargeCodeConfig}
                quoteDetail={quoteRequestDetails}
                currencies={currencies}
                chargeName={chargeName}
                currentIndex={index}
                remove={remove}
                IsChargeableWeightRequired={IsChargeableWeightRequired}
              />
            ))}
        </tbody>
      </table>

      <div className="d-flex justify-content-between align-items-center mt-3">
        <Button
          variant="primary"
          className="rounded-pill blue-200 h-26 mb-3"
          label={intl.formatMessage({
            id: "quotation-form.addCharge",
            defaultMessage: "Add charge",
          })}
          onClick={handleAddChargeClick}
        />
        {currentChargeCosts[chargeName].isCostLoading ? (
          <div className="skeleton-card" style={{ width: 110 }}>
            <Row className="skeleton-row">
              <span key={6} className="skeleton full" />
            </Row>
          </div>
        ) : (
          <span>
            <FormattedMessage id="quotation-form.subtotal" defaultMessage="Subtotal" />:{" "}
            <b>
              {roundUp(currentChargeCosts[chargeName].subTotal)} {shipperPreferredCurrency}
            </b>
          </span>
        )}
      </div>
    </div>
  );
}

export default CostFormCharges;
